import React from 'react';
import { Button } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import type { FallbackProps } from 'react-error-boundary';
import { Outlet, useLocation } from 'react-router-dom';

import DefaultFallback from '@components/Fallbacks/DefaultFallback';

/**
 * The tab's own error state, rendered where the tab body would be. Deliberately says nothing
 * about the underlying error — this is a dealer-facing surface inside someone else's website —
 * and the error itself is logged by the boundary's `onError`, never shown.
 */
const TabErrorFallback: React.FC<FallbackProps> = ({ resetErrorBoundary }) => (
  <DefaultFallback
    severity='error'
    message="This tab couldn't be displayed."
    action={
      <Button color='inherit' size='small' onClick={resetErrorBoundary}>
        Try again
      </Button>
    }
  />
);

/**
 * A pathless route element that scopes a render fault to the tab it happened in.
 *
 * Mount it as the only child of a product's layout route, with the tab routes beneath it:
 * the shell (its band, header actions and tab strip) renders above the boundary, so a tab that
 * throws degrades to a message in the outlet with the strip still usable, instead of unmounting
 * the whole app into `App.tsx`'s full-page fallback. Inside an iframe that fallback is a dead
 * end — the dealer has no address bar to recover with.
 *
 * `resetKeys` on the pathname matters because ONE boundary instance serves all of a product's
 * tabs: without it, a fault on one tab would keep showing after the user clicked to the next.
 * Try again re-mounts the subtree (`react-error-boundary` remounts children on reset).
 *
 * Valet's Orders tab keeps `ValetOrdersTabErrorBoundary` from the shared host package inside
 * this one — it has its own copy and resets on the rooftop; the inner boundary catches first.
 */
const TabErrorBoundary: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary
      FallbackComponent={TabErrorFallback}
      resetKeys={[pathname]}
      onError={(error, info) => {
        console.error('Tab render error:', error, info);
      }}
    >
      <Outlet />
    </ErrorBoundary>
  );
};

export default TabErrorBoundary;
